import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { AlertTriangle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { systemAuditService } from '../../services/systemAuditService';

import ErrorList from './components/ErrorList';
import ErrorFilters from './components/ErrorFilters';
import NotificationSettings from './components/NotificationSettings';
import Icon from '../../components/AppIcon';


export default function ErrorMonitoringCenter() {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({
    totalErrors: 0,
    criticalErrors: 0,
    unresolvedErrors: 0,
    errorRate: 0
  });
  const [filters, setFilters] = useState({
    severity: [],
    errorType: null,
    resolutionStatus: 'unresolved',
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
    endDate: new Date()?.toISOString()
  });
  const [activeTab, setActiveTab] = useState('errors');

  useEffect(() => {
    loadErrorData();
    setupRealtimeSubscriptions();
  }, [filters]);

  const loadErrorData = async () => {
    try {
      setLoading(true);

      // Load errors
      const errorsData = await systemAuditService?.getSystemErrors({
        severity: filters?.severity,
        errorType: filters?.errorType,
        resolutionStatus: filters?.resolutionStatus,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        limit: 100
      });
      setErrors(errorsData);

      // Load alerts
      const alertsData = await systemAuditService?.getSuperAdminAlerts({
        alertType: ['critical_error', 'system_failure'],
        status: 'active',
        limit: 20
      });
      setAlerts(alertsData);

      // Calculate stats
      const totalErrors = errorsData?.length || 0;
      const criticalErrors = errorsData?.filter(e => e?.severity === 'critical')?.length || 0;
      const unresolvedErrors = errorsData?.filter(e => e?.resolutionStatus === 'unresolved')?.length || 0;
      const errorRate = totalErrors > 0 ? ((criticalErrors / totalErrors) * 100)?.toFixed(2) : 0;

      setStats({
        totalErrors,
        criticalErrors,
        unresolvedErrors,
        errorRate
      });
    } catch (error) {
      console.error('Error loading error data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    const channel = supabase?.channel('error-monitoring-changes')?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'system_errors' },
        () => {
          loadErrorData();
        }
      )?.on('postgres_changes',
        { event: '*', schema: 'public', table: 'super_admin_alerts' },
        () => {
          loadErrorData();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  };

  const handleResolveError = async (errorId, notes) => {
    try {
      await systemAuditService?.updateErrorStatus(errorId, 'resolved', notes);
      loadErrorData();
    } catch (error) {
      console.error('Error resolving error:', error);
    }
  };

  const StatCard = ({ title, value, icon: Icon, bgColor, trend }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
          {trend && (
            <p className="text-xs text-gray-500 mt-2">{trend}</p>
          )}
        </div>
        <div className={`${bgColor} p-3 rounded-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Helmet>
        <title>Error Monitoring Center - System Health</title>
      </Helmet>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Error Monitoring Center</h1>
              <p className="text-gray-600">Comprehensive error tracking with automated Super Admin notifications</p>
            </div>
            <button
              onClick={loadErrorData}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Errors"
            value={stats?.totalErrors}
            icon={AlertCircle}
            bgColor="bg-blue-600"
            trend="Last 7 days"
          />
          <StatCard
            title="Critical Errors"
            value={stats?.criticalErrors}
            icon={XCircle}
            bgColor="bg-red-600"
            trend={`${stats?.errorRate}% of total`}
          />
          <StatCard
            title="Unresolved"
            value={stats?.unresolvedErrors}
            icon={AlertTriangle}
            bgColor="bg-orange-600"
            trend="Requires attention"
          />
          <StatCard
            title="Active Alerts"
            value={alerts?.length}
            icon={AlertTriangle}
            bgColor="bg-yellow-600"
            trend="Super Admin notified"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('errors')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'errors' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Error Dashboard
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'notifications'
                    ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Notification Settings
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'errors' && (
              <div>
                <ErrorFilters filters={filters} onFiltersChange={setFilters} />
                <ErrorList errors={errors} onResolve={handleResolveError} />
              </div>
            )}
            {activeTab === 'notifications' && (
              <NotificationSettings />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}